import { useId } from 'react';
import { splinePath } from '../lib/chart';

/** Enough resolution for a 1000px-wide plot without a megabyte of path data. */
export const CHART_POINTS = 180;

/** Vertical breathing room so a zero-valued line clears the axis. */
const PLOT_INSET = 4;

export interface Series {
  values: readonly number[];
  ceiling: number;
  color: string;
  width: number;
  /** Fill the area under the line with a fade of `color`. */
  area?: boolean;
}

/**
 * The design's chart body: four dashed gridlines, a solid baseline, and one
 * spline per series drawn into a 1000-unit-wide viewBox that stretches to the
 * panel. `vector-effect="non-scaling-stroke"` is what keeps the strokes an
 * even weight despite the non-uniform scaling.
 */
export function SeriesChart({
  height,
  series,
  gridlines = 4,
}: {
  height: number;
  series: readonly Series[];
  gridlines?: number;
}) {
  const uid = useId().replace(/:/g, '');
  const rows = Array.from(
    { length: gridlines },
    (_, i) => 1 + (i * (height - 2)) / gridlines,
  );
  // Series are drawn into a slightly shorter box and nudged up off the
  // baseline: an idle host reads 0% for minutes at a time, and a stroke
  // centred exactly on the axis loses half its width to the clip edge.
  const plot = height - PLOT_INSET;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 1000 ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <defs>
        {series.map((s, i) =>
          s.area ? (
            <linearGradient key={i} id={`fill-${uid}-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.20" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0" />
            </linearGradient>
          ) : null,
        )}
      </defs>

      {rows.map((y) => (
        <line
          key={y}
          x1="0"
          y1={y}
          x2="1000"
          y2={y}
          stroke="var(--line)"
          strokeWidth="1"
          strokeDasharray="3 5"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <line
        x1="0"
        y1={height - 1}
        x2="1000"
        y2={height - 1}
        stroke="var(--line)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />

      <g transform={`translate(0 ${-PLOT_INSET / 2})`}>
        {series.map((s, i) =>
          s.area ? (
            <path
              key={`a${i}`}
              d={splinePath(s.values, 1000, plot, s.ceiling, true)}
              fill={`url(#fill-${uid}-${i})`}
              transform={`translate(0 ${PLOT_INSET})`}
            />
          ) : null,
        )}
        {series.map((s, i) => (
          <path
            key={`l${i}`}
            d={splinePath(s.values, 1000, plot, s.ceiling)}
            fill="none"
            stroke={s.color}
            strokeWidth={s.width}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            transform={`translate(0 ${PLOT_INSET})`}
          />
        ))}
      </g>
    </svg>
  );
}

/** The 88×20 CPU trend drawn in each process row. */
export function Sparkline({
  values,
  ceiling,
}: {
  values: readonly number[];
  ceiling: number;
}) {
  return (
    <svg
      width="88"
      height="20"
      viewBox="0 0 88 20"
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      <path
        d={splinePath(values, 88, 20 - PLOT_INSET, ceiling)}
        fill="none"
        stroke="var(--pos)"
        strokeWidth="1.6"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        transform={`translate(0 ${PLOT_INSET / 2})`}
      />
    </svg>
  );
}

/** Vertical value scale to the left of a chart. */
export function YAxis({
  labels,
  bottomPad = 20,
}: {
  labels: readonly string[];
  bottomPad?: number;
}) {
  return (
    <div
      style={{
        width: 34,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        fontSize: 11,
        color: 'var(--faint)',
        padding: `0 0 ${bottomPad}px`,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
      }}
    >
      {labels.map((label) => (
        <div key={label}>{label}</div>
      ))}
    </div>
  );
}

export function XAxis({ labels }: { labels: readonly string[] }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 8,
        fontSize: 11.5,
        color: 'var(--faint)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {labels.map((label, i) => (
        <div key={`${label}-${i}`}>{label}</div>
      ))}
    </div>
  );
}
